/**
 * Certification Grouping Configuration
 * 
 * Maps certification courses to their display groups based on Cert-group.md
 * Groups are displayed in priority order with Angelwatch Core at the top
 */

// Group definitions with priority order
export const GROUP_DEFINITIONS = [
  {
    name: "Angelwatch Core",
    priority: 1,
    keys: [
      "daily_protocols_exterior_threat",
      "interior_active_shooter_procedures",
      "counter_measures_co2_introduction",
      "co2_procedures_protocols_for_room_with_secondary_egress",
      "active_shooter_without_2nd_egress_shelter_in_place",
      "access_control_procedures_radio_communication_shelter_in_place_communication",
      "combative_parent_access_control",
      "congratulations_on_completing_your_course",
    ],
  }
];

// Create a map of courseKey -> groupName for quick lookup
const COURSE_KEY_TO_GROUP = {};
GROUP_DEFINITIONS.forEach((group) => {
  group.keys.forEach((key) => {
    COURSE_KEY_TO_GROUP[key.toLowerCase()] = group.name;
  });
});

// Fallback group for courses not in the map
const DEFAULT_GROUP = "Other Certifications";
const DEFAULT_PRIORITY = 999;

/**
 * Get the group name for a certification based on its courseKey
 */
export const getGroupForCertification = (cert) => {
  const courseKey = (cert.courseKey || cert.key || cert.course?.key || "").toLowerCase();

  // Check metadata section first (for section_v -> core_active_shooter mapping)
  const section = cert.metadata?.section || cert.section || "";
  if (section.toLowerCase() === "section_v") {
    return "Angelwatch Core";
  }

  // Check direct mapping
  if (COURSE_KEY_TO_GROUP[courseKey]) {
    return COURSE_KEY_TO_GROUP[courseKey];
  }

  // Check displayGroup from backend if available
  if (cert.displayGroup) {
    return cert.displayGroup;
  }

  return DEFAULT_GROUP;
};

/**
 * Group certifications by their category/group
 * Returns an object with group names as keys and arrays of certifications as values
 */
export const groupCertifications = (certifications = []) => {
  const grouped = {};

  certifications.forEach((cert) => {
    const groupName = getGroupForCertification(cert);
    if (!grouped[groupName]) {
      grouped[groupName] = {
        name: groupName,
        certifications: [],
        priority: GROUP_DEFINITIONS.find(g => g.name === groupName)?.priority || DEFAULT_PRIORITY,
      };
    }
    grouped[groupName].certifications.push(cert);
  });

  return grouped;
};

/**
 * Get sorted group names in priority order
 */
export const getSortedGroupNames = (groupedCertifications = {}) => {
  const groupNames = Object.keys(groupedCertifications);

  return groupNames.sort((a, b) => {
    const priorityA = GROUP_DEFINITIONS.find(g => g.name === a)?.priority || DEFAULT_PRIORITY;
    const priorityB = GROUP_DEFINITIONS.find(g => g.name === b)?.priority || DEFAULT_PRIORITY;

    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }

    // If same priority, sort alphabetically
    return a.localeCompare(b);
  });
};

/**
 * Group courses by their category/group
 * Similar to groupCertifications but for course objects
 */
export const groupCourses = (courses = []) => {
  const grouped = {};

  courses.forEach((course) => {
    const courseKey = (course.courseKey || course.key || course.id || course._id || "").toLowerCase();
    let groupName = COURSE_KEY_TO_GROUP[courseKey];

    // Check displayGroup from backend if available
    if (!groupName && course.displayGroup) {
      groupName = course.displayGroup;
    }

    if (!groupName) {
      groupName = DEFAULT_GROUP;
    }

    if (!grouped[groupName]) {
      grouped[groupName] = {
        name: groupName,
        courses: [],
        priority: GROUP_DEFINITIONS.find(g => g.name === groupName)?.priority || DEFAULT_PRIORITY,
      };
    }
    grouped[groupName].courses.push(course);
  });

  // Sort courses within each group (optional - by title)
  Object.keys(grouped).forEach((groupName) => {
    grouped[groupName].courses.sort((a, b) => {
      const titleA = (a.title || a.name || a.courseTitle || "").toLowerCase();
      const titleB = (b.title || b.name || b.courseTitle || "").toLowerCase();
      return titleA.localeCompare(titleB);
    });
  });

  return grouped;
};

/**
 * Get all group names in priority order
 */
export const getAllGroupNames = () => {
  return GROUP_DEFINITIONS.map(g => g.name);
};

/**
 * Get group names that have courses/certifications
 */
export const getAvailableGroupNames = (groupedData = {}) => {
  const available = Object.keys(groupedData).filter(
    groupName => groupedData[groupName]?.courses?.length > 0 || groupedData[groupName]?.certifications?.length > 0
  );

  return available.sort((a, b) => {
    const priorityA = GROUP_DEFINITIONS.find(g => g.name === a)?.priority || DEFAULT_PRIORITY;
    const priorityB = GROUP_DEFINITIONS.find(g => g.name === b)?.priority || DEFAULT_PRIORITY;
    return priorityA - priorityB;
  });
};

